import React, { useMemo } from "react";
import Select, { GroupBase, StylesConfig } from "react-select";
import { ResolutionKeys } from "../types";

// Base option interface for react-select
interface Option {
  value: string;
  label: string;
  category: string;
}

interface DataSets {
  names: string[];
  titles: string[];
  categories: string[];
}

interface MapSelectorProps {
  data: DataSets;
  onSelect: (option: Option | null) => void;
  baseValue: string | null;
  is_clearable: boolean;
  available_resolutions: ResolutionKeys[];
}

const MapSelector: React.FC<MapSelectorProps> = ({
  data,
  onSelect,
  baseValue,
  is_clearable,
}) => {
  // Convert your data format to react-select format if provided
  const options = useMemo(() => {
    if (data) {
      // Create a map to organize items by category
      const categorizedItems = new Map<string, Option[]>();
      
      // Process each item
      for (let i = 0; i < data.names.length; i++) {
        const name = data.names[i];
        const title = data.titles[i];
        const category = data.categories[i];
        
        // Create option
        const option: Option = {
          value: name,
          label: title || name, // Use title as label if available, otherwise use name
          category: category,
        };
        
        // Add to appropriate category
        if (!categorizedItems.has(category)) {
          categorizedItems.set(category, []);
        }
        categorizedItems.get(category)?.push(option);
      }
      
      // Convert map to array with format expected by react-select for grouping
      return Array.from(categorizedItems.entries()).map(([category, options]) => ({
        label: category,
        options: options
      }));
    }
    
    return [];
  }, [data]);

  // Custom styles for react-select
  const customStyles: StylesConfig<Option, false, GroupBase<Option>> = {
    group: (provided) => ({
      ...provided,
      paddingTop: 8,
      paddingBottom: 0,
    }),
    groupHeading: (provided) => ({
      ...provided,
      fontWeight: 'bold',
      fontSize: '14px',
      color: '#333',
      marginBottom: '8px',
      paddingTop: '8px',
      paddingBottom: '8px',
      paddingLeft: '8px',
      paddingRight: '8px',
      position: 'relative',
      textTransform: 'none', // Override the default uppercase transformation
      // Create a border that doesn't extend to the edges
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '8px',
        right: '8px',
        height: '1px',
        backgroundColor: '#ccc'
      }
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999 // Very high z-index
    })
  };

  return (
    <Select
      key={`${baseValue}-${data.names.length}-${data.names.join(',')}`}
      options={options}
      onChange={onSelect}
      placeholder="Search in dataset..."
      className="searchable-selector"
      defaultValue={baseValue ? {
        label: data.titles[data.names.indexOf(baseValue)], 
        value: baseValue, 
        category: data.categories[data.names.indexOf(baseValue)]
      } : null}
      isClearable={is_clearable}
      styles={customStyles}
      menuPortalTarget={document.body} // This ensures the menu renders at the document body level
      menuPosition={'fixed'} // Use fixed positioning for the menu
      menuPlacement={'auto'} // Automatically determine menu placement
    />
  );
};

export default MapSelector;